/**
 * Génération des planches PDF d'étiquettes résidents.
 *
 * Trois points qui comptent pour la maintenance :
 *
 * 1. `jspdf` et `qrcode` sont chargés en IMPORT DYNAMIQUE. Ces deux librairies
 *    touchent `window` et pèsent ~400 kB à elles deux : l'import dynamique les
 *    garde hors du bundle initial, que webpack 4 découpe correctement.
 *    Versions à respecter — jspdf 2.5.x et qrcode 1.5.x. jsPDF 3 et 4 publient
 *    un champ `exports` que webpack 4 (react-scripts 4.0.3) ne sait pas
 *    résoudre, et leurs `engines` excluent Node 14.
 *
 * 2. Les QR codes sont mis en cache par identifiant : sans cache, une planche
 *    de 24 étiquettes identiques encode 24 fois le même QR, soit 240 encodages
 *    inutiles sur 10 pages.
 *
 * 3. La géométrie vient exclusivement de `labelLayout.js`.
 */

import {
  LABEL_WIDTH_MM,
  LABEL_HEIGHT_MM,
  LABELS_PER_PAGE,
  labelOrigin,
} from "./labelLayout";

/** Marge interne de l'étiquette, en mm. */
const PADDING_MM = 4;
/** Côté du QR code, en mm. */
const QR_SIZE_MM = 20;
/** Gouttière entre la colonne de texte et le QR, en mm. */
const GUTTER_MM = 2;

/** Les deux mises en page proposées à l'impression. */
export const LAYOUTS = ["compact", "framed"];

async function loadDeps() {
  const [jspdf, qrcode] = await Promise.all([import("jspdf"), import("qrcode")]);
  // jspdf 2.x expose `jsPDF` en nommé ; qrcode est CommonJS, d'où le `default`.
  const jsPDF = jspdf.jsPDF || jspdf.default;
  const QRCode = qrcode.default || qrcode;
  return { jsPDF, QRCode };
}

function createQrCache(QRCode) {
  const cache = new Map();
  return function getQr(text) {
    if (!cache.has(text)) {
      cache.set(
        text,
        QRCode.toDataURL(text, {
          margin: 0,
          width: 200,
          errorCorrectionLevel: "M",
          color: { dark: "#000000", light: "#ffffff" },
        })
      );
    }
    return cache.get(text);
  };
}

/**
 * Dessine une étiquette.
 *
 *   ligne 1 — NOM        gras 12 pt
 *   ligne 2 — Prénom     normal 12 pt
 *   ligne 3 — Résidence  normal 8 pt, tronquée à la largeur disponible
 *   ligne 4 — WOSH-ID    gras 10 pt, calé en bas
 *   à droite — QR 20 × 20 mm encodant le WOSH-ID, centré verticalement
 *
 * Aucun montant n'est imprimé : ces étiquettes servent uniquement à identifier
 * le linge d'une personne.
 */
function drawLabel(doc, x, y, resident, getQr, layout) {
  if (layout === "framed") {
    doc.setDrawColor(150, 150, 150);
    doc.setLineWidth(0.2);
    doc.roundedRect(x + 0.5, y + 0.5, LABEL_WIDTH_MM - 1, LABEL_HEIGHT_MM - 1, 1.5, 1.5);
  }

  const textLeft = x + PADDING_MM;
  const textRight = x + LABEL_WIDTH_MM - PADDING_MM - QR_SIZE_MM - GUTTER_MM;
  const maxTextWidth = textRight - textLeft;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(20, 20, 20);
  doc.text(doc.splitTextToSize(String(resident.lastName || ""), maxTextWidth)[0] || "", textLeft, y + 8);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(40, 40, 40);
  doc.text(doc.splitTextToSize(String(resident.firstName || ""), maxTextWidth)[0] || "", textLeft, y + 14);

  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(doc.splitTextToSize(String(resident.residence || ""), maxTextWidth)[0] || "", textLeft, y + 20);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(20, 20, 20);
  doc.text(String(resident.woshId || ""), textLeft, y + 29);

  const qrX = x + LABEL_WIDTH_MM - PADDING_MM - QR_SIZE_MM;
  const qrY = y + (LABEL_HEIGHT_MM - QR_SIZE_MM) / 2;
  doc.addImage(getQr(String(resident.woshId || "")), "PNG", qrX, qrY, QR_SIZE_MM, QR_SIZE_MM);
}

/** Traits de coupe légers, utiles sur du papier vierge plutôt que planche pré-découpée. */
function drawCutMarks(doc) {
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.1);
  for (let i = 0; i < LABELS_PER_PAGE; i++) {
    const { x, y } = labelOrigin(i);
    doc.rect(x, y, LABEL_WIDTH_MM, LABEL_HEIGHT_MM);
  }
}

function createDoc(jsPDF) {
  return new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
}

/**
 * Mode « planches pleines » : chaque résident occupe `resident.pages` planches
 * complètes de 24 étiquettes identiques.
 *
 * @param {Array<{woshId,firstName,lastName,residence,pages}>} residents
 * @param {{cutMarks?: boolean, layout?: "compact"|"framed"}} [options]
 */
export async function generateFullPagesPDF(residents, options = {}) {
  const { jsPDF, QRCode } = await loadDeps();
  const getQr = createQrCache(QRCode);
  const doc = createDoc(jsPDF);
  const layout = options.layout || "compact";

  // Les QR sont résolus en amont : `doc.addImage` est synchrone, garder un
  // `await` dans la double boucle sérialiserait 240 encodages inutilement.
  const qrByResident = new Map();
  for (const resident of residents) {
    const key = String(resident.woshId || "");
    if (!qrByResident.has(key)) qrByResident.set(key, await getQr(key));
  }
  const resolvedQr = (key) => qrByResident.get(String(key || ""));

  let isFirstPage = true;
  residents.forEach((resident) => {
    const pageCount = Math.max(1, Number(resident.pages) || 1);
    for (let p = 0; p < pageCount; p++) {
      if (!isFirstPage) doc.addPage();
      isFirstPage = false;
      if (options.cutMarks) drawCutMarks(doc);
      for (let i = 0; i < LABELS_PER_PAGE; i++) {
        const { x, y } = labelOrigin(i);
        drawLabel(doc, x, y, resident, resolvedQr, layout);
      }
    }
  });

  return doc;
}

/**
 * Mode « planche entamée » : une seule planche, chaque case pouvant être vide
 * ou attribuée à un résident différent. Évite de gâcher une feuille complète
 * pour trois étiquettes.
 *
 * @param {Array<object|null>} cells 24 entrées (résident ou null)
 * @param {{cutMarks?: boolean, layout?: "compact"|"framed"}} [options]
 */
export async function generateLabelsPDF(cells, options = {}) {
  const { jsPDF, QRCode } = await loadDeps();
  const getQr = createQrCache(QRCode);
  const doc = createDoc(jsPDF);
  const layout = options.layout || "compact";

  if (options.cutMarks) drawCutMarks(doc);

  const used = cells.slice(0, LABELS_PER_PAGE);
  const qrByResident = new Map();
  for (const resident of used) {
    if (!resident) continue;
    const key = String(resident.woshId || "");
    if (!qrByResident.has(key)) qrByResident.set(key, await getQr(key));
  }
  const resolvedQr = (key) => qrByResident.get(String(key || ""));

  used.forEach((resident, index) => {
    if (!resident) return;
    const { x, y } = labelOrigin(index);
    drawLabel(doc, x, y, resident, resolvedQr, layout);
  });

  return doc;
}

/**
 * Ouvre le PDF dans un nouvel onglet, avec repli sur téléchargement direct.
 * Un `window.open` sec est silencieusement bloqué par Safari et par la plupart
 * des bloqueurs de popups — le repli n'est pas décoratif.
 */
export function openOrDownloadPdf(doc, filename = "etiquettes.pdf") {
  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank");
  if (!win) {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  // Pas de séparateur numérique (60_000) : Babel de react-scripts 4 ne le transpile pas.
  setTimeout(function () {
    URL.revokeObjectURL(url);
  }, 60000);
}
